import CreditCard from "../scripts/paycentral.ui.paymentmethods.creditcard.js";
import DirectDebit from './widget.directdebit.js';
import Mandate from './widget.mandate.js';
import Iats3dsController from "../scripts/3ds/providers/iats3ds.js";
import * as threeds from "../scripts/3ds/main.js";
import utilities from "../scripts/3ds/providers/utilities/utilities.js";
import * as googleRecaptcha from "../scripts/paycentral.ui.google.recaptcha.js";
import { PayCentralURL } from "../scripts/utils/call-api.js";

class Widget {
    /**
     * @param {*} submitButton Button used to submit the form to DV
     * @param {*} token Payment token to be passed back to iMIS
     * @param {*} json JSON object received from iMIS containing required payment information
     * @param {*} validation String containing the validation errors to be passed back to iMIS, displayed under the iFrame
     * @param {*} errors List of elements that have the error class, there should be none for a successful token
     * @param {*} frame Determines which view is being displayed in the iFrame
     * @param {*} capture Capture Result
     * @param {*} transactionIntentId Transaction Intent Id used for Immediate Capture
     */
    constructor(submitButton, token, json, validation, errors, frame, capture, transactionIntentId) {
        this.submitButton = submitButton || document.getElementById("vaultSubmit"); // Button used to submit the form to DV
        this.token = token || document.getElementById("responseToken"); // Payment token to be passed back to iMIS
        this.json = json || document.getElementById("json"); // JSON object received from iMIS containing required payment information
        this.validation = validation || document.getElementById("validationMsg"); // String containing the validation errors to be passed back to iMIS, displayed under the iFrame
        this.errors = errors || document.getElementsByClassName("input-validation-error"); // List of elements that have the error class, there should be none for a successful token
        this.frame = frame || this.getFrameData(); // Determines which view is being displayed in the iFrame
        this.capture = capture || document.getElementById("captureResult");
        this.transactionIntentId = transactionIntentId || document.getElementById("TransactionIntentId");
        this.threedsCode = document.getElementById("ThreeDSCode");
        this.nextActionData = document.getElementsByName("next-action-data")[0];
        this.debugEnabled = true;

        // Catches the post sent from iMIS
        window.addEventListener("message", (e) => {
            this.tryReceivePostMessage(e);
        });

        const hasValidationErrors = this.errors.length > 0 || this.validation?.value.length > 0;
        const hasTokenizationResult = this.token?.value.length > 0;
        const hasTransactionIntentId = this.transactionIntentId?.value.length > 0;
        const requiresAction = (this.nextActionData && this.nextActionData.dataset.type === "ThreeDsChallenge");
        const hasSubmitFormResult = !requiresAction && (hasValidationErrors || hasTransactionIntentId || hasTokenizationResult);

        if (this.nextActionData?.dataset?.type) {
            console.log("required action:", this.nextActionData?.dataset?.type); //TODO: REMOVE ME
        }

        if (hasSubmitFormResult) {
            // Dispatch result messages found after form POST.
            //TODO: We dont really need to have postOutboundMessage() 3 times, but this was my POC to make sure everything worked exactly as expected before trying to make it look good.
            if (hasValidationErrors)
                this.handleFailure(this.getValidationErrorMessage());

            if (hasTokenizationResult || hasTransactionIntentId) { console.log("... checkout process completed."); } //TODO: REMOVE ME 

            if (hasTokenizationResult)
                this.postOutboundMessage(this.getTokenizationSuccessMessage());

            if (hasTransactionIntentId)
                this.postOutboundMessage(this.getReadyToCaptureMessage());
        }

        if (requiresAction) {
            this.handleNextActionData();
        }

        // Initialize the widget.
        if (!hasSubmitFormResult || hasValidationErrors) {
            this.initialize();
        }
    }

    /**
     * Get target origin.
     */
    getTargetOrigin() {
        let url = "";
        try {
            url = document.location.ancestorOrigins ? document.location.ancestorOrigins[0] :
                this.frame[2] ? "https://" + this.frame[2] : "*";

            if (url === undefined || url === "*") {
                let referrer = document.referrer.match(/^https:\/\/[^/]+/)[0];
                let imisurl = document.location.origin !== referrer ? referrer : "*";
                if (imisurl === "*") {
                    console.log("widget getTargetOrigin failed to find imis url");
                    return "*";
                }
                url = imisurl;
            }
            return url.match(/^https:\/\/[^/]+/)[0];
        }
        catch (e) {
            console.error("PayCentral widget must be loaded using https. Url=" + url, "Source: widget getTargetOrigin", e);
            return "*";
        }
    }

    /**
     * Initialize the appropriate payment method class so the user can enter their information to
     * begin the checkout process or fix validation errors that have been found. If we have a 
     * tokenization result or a transaction intent id we can skip initialization.
     * Initialize widget payment method.
     */
    initialize() {

        if (!this.submitButton) return;

        // Set iframe background color styles.
        const color = getComputedStyle(document.querySelector('body > form > div > input:first-of-type')).getPropertyValue('background-color');
        document.documentElement.style.setProperty('--bg-color', color);

        // Initialize the appropriate payment method class.
        switch (this.frame[0]) {
            case "creditcard":
                if (document.getElementById("payment-method__credit-card")) {
                    this.card = new CreditCard();
                }
                break;
        case "directdebit":
            this.bank = new DirectDebit(null, null, this.frame[1], null, null, null, false); //TODO: This constructor is... odd.
            break;
        case "mandate":
            this.mandate = new Mandate(); //TODO: Should this be here, or in its own js or view-js?
            break;
        default:
            throw new Error("Error: DataVault could not determine which payment frame to serve. The requested frame value was: " + this.frame[0]);
        }
    }

    /**
     * Attempt a submit.
     * The caller will be notified of any unexpected errors.
     *
     * @param {object} data Contains transaction and payer data
     * @param {object} widget Contains widget and payment method data
     */
    async attemptSubmit(data, widget) {
        try {
            //TODO: I'm not sure if this should live here but for now it's fine.
            await googleRecaptcha.invokeChallengeAsync("checkout");
            console.log("Begin checkout process..."); //TODO: REMOVE ME
            console.log("current action:", "CREATE CHECKOUT"); //TODO: REMOVE ME
            await this.submit(data, widget);
        } catch (e) {
            this.notifyCallerOfError((e instanceof Error) ? e.message : String(e));
        }
    }

    notifyCallerOfError(errorMessage) {
        const msg = `dv-response:PayCentral Error: ${errorMessage}`;
        window.parent.postMessage(msg, this.getTargetOrigin());
    }

    /**
     * Submit.
     * @param {object} data Contains transaction and payer data
     * @param {object} widget Contains widget and payment method data
     */
    async submit(data) {
        const widget = { card: this.card, frame: this.frame };

        // Populate form data.
        this.populateFormData(data);

        // External payment methods (e.g. Stripe)
        if (window.paycentralstripe) {
            return window.paycentralstripe.handleNextAction("submit", data);
        }

        // If Direct Debit nothing more to do
        if (!widget.card)
            return this.clickSubmit();
        
        this.handleSubmitCardAccountUnsafe(data, widget);
    }


    async handleSubmitCardAccountUnsafe(data, widget) {
        // Ensure incoming payload data is properly formatted and backward compatible.
        data = this.ensureValidData(data);

        // If we are coming from gift entry we have a staff user and do not want to trigger 3ds.
        if (!data.paymentRequestData || !data.paymentRequestData.TransactionDetails) {
            this.log("3DS authentication not required."); //TODO-AWELLS: Debug helper - can remove.
            return this.clickSubmit();
        }

        //TODO-AWELLS: Lets move this to widget.creditcard.js - something like the existing getCardData() method.
        // populate card data.
        const payer = data.paymentRequestData.TransactionDetails.Payer;
        const card = widget.card.getCardData();
        card.Address = payer.BillingAddress;
        card.ContactName = payer.CardholderName;

        // Checks to see if the payment method and currency have 3DS capability, 
        // then checks to see if we have configured the gateway provider to support 3DS.
        if (!threeds.hasCapability(data))
            return this.clickSubmit();

        const provider3ds = threeds.factory.createProvider(data);

        //TODO: Doubling down on the 'todo' comment right below this!
        //TODO: This whole section needs to be moved ot the main 3ds module at some point so this doesnt become even more insane.
        if (provider3ds.provider === "iats" && window.iats3ds !== undefined) {

            // iATS 3DS.
            const iats3dsSdk = new Iats3dsController(widget.card, data);
            const errors = iats3dsSdk.validate();
            if (errors) {
                this.handleFailure(errors);
                return;
            }
            iats3dsSdk.iatsGetFingerPrintId().then(response => {
                if (response.IsAuthenticated) {
                    const authenticationCode3DS = document.getElementById("ThreeDSCode");
                    authenticationCode3DS.value = response.ThreeDSCode;
                    this.clickSubmit();
                } else if (response.Challenge) {
                    // challenge is handled on the iMIS side
                    this.handleChallenge(response.Challenge);
                    return;
                } else {
                    this.handleFailure(response.Errors);
                    return;
                }

            });
        } else if (provider3ds.provider === "globalpayments") {
            // OpenEdge 3DS.
            const result = await provider3ds.authenticate(card, data.paymentRequestData);
            if (result.error) {
                this.handleFailure(result.errorMsg);
            }
            if (result.action === "CHALLENGE") {
                this.handleChallenge(result);
            }
            if (result.action === "COMPLETE") {
                this.clickSubmit();
            }
        } else if (provider3ds.provider === "stripe") {
            // Stripe 3DS.
            console.group('Stripe 3DS');

            const result = await provider3ds.authenticate(card, data.paymentRequestData);

            if (result.error) {
                this.handleFailure(result.error);
            }

            console.log("requires additional action:", result.action);

            if (result.action === "CHALLENGE") {
                this.handleChallenge(result);

                this.disableForm(true);
            }
            if (result.action === "COMPLETE") {
                this.clickSubmit();
            }

            console.groupEnd();
        } else {
            return this.clickSubmit();
        }
    }

    /**
     * The all powerful submit button clicker thingy.
     */
    clickSubmit() {
        const btn = document.getElementById("vaultSubmit");
        btn.disabled = false;
        btn.click();
        btn.disabled = true;
    }

    /**
    * Populates form inputs with transaction data values.
    * @param {object} sessionData - Data used to populate form
    */
    populateFormData(sessionData) {

        utilities.setTargetValue("DvGatewayID", sessionData.DvGatewayID);
        utilities.setTargetValue("AccountType", sessionData.AccountType);
        utilities.setTargetValue("Amount", sessionData.Amount);
        utilities.setTargetValue("Currency", sessionData.Currency);

        if (sessionData.Name) {
            utilities.setTargetValue("Name_FirstName", sessionData.Name.FirstName);
            utilities.setTargetValue("Name_LastName", sessionData.Name.LastName);
            utilities.setTargetValue("Name_FullName", sessionData.Name.FullName);
        }

        if (sessionData.Address) {
            utilities.setTargetValue("Address_CityName", sessionData.Address.CityName);
            utilities.setTargetValue("Address_CountryCode", sessionData.Address.CountryCode);
            utilities.setTargetValue("Address_CountryName", sessionData.Address.CountryName);
            utilities.setTargetValue("Address_CountrySubEntityCode", sessionData.Address.CountrySubEntityCode);
            utilities.setTargetValue("Address_CountrySubEntityName", sessionData.Address.CountrySubEntityName);
            utilities.setTargetValue("Address_PostalCode", sessionData.Address.PostalCode);

            for (let i = 0; i < sessionData.Address.AddressLines.length; i++) {

                const exists = utilities.setTargetValue(`Address_AddressLines_${i}_`, sessionData.Address.AddressLines[i]);
                if (exists === false) {
                    // Add an input and set the value.
                    const input = document.createElement("input");
                    input.setAttribute("type", "hidden");
                    input.setAttribute("name", `Address.AddressLines[${i}]`);
                    input.setAttribute("id", `Address_AddressLines_${i}_`);
                    input.setAttribute("value", sessionData.Address.AddressLines[i]);

                    const addressTargetElement = document.getElementById("BillingAddress");
                    if (addressTargetElement) {
                        addressTargetElement.appendChild(input);
                    }
                }
            }
        }

        const isRecurring = utilities.convertToBool(sessionData.IsRecurring);
        if (isRecurring != undefined) {
            utilities.setTargetValue("IsRecurring", isRecurring);
        }
    }

    /**
     * Get form element data-attribute value.
     */
    getFrameData() {
        let data = document.querySelector('form[data]');
        if (data)
            return data.getAttribute('data').split('#');;
    }

    /**
    * Console log message if debug is enabled.
    */
    log(message) {
        if (this.debugEnabled) console.log(message);
    }

    /**
     * Ensure incoming payload data is properly formatted and backward compatible.
     * Note: The incoming request format changed following version <x.121> - if the payload has
     * not been updated to match the standard format we will exit here and prevent 3D Secure
     * authentication from being initiated.
     * @param {object} data - data received from imis/the sdk.
     */
    ensureValidData(data) {
        if (data.paymentRequest) {
            // Update payloads received from iMIS versions <x.121> and earlier.
            data.paymentRequestData = {
                PaymentInstrument: {
                    MethodData: {
                        Name: null,
                        Type: "CreditCard", // known constant value if this method is called.
                        GatewayId: data.DvGatewayID,
                        GatewayProvider: data.GatewayProvider,
                        Supports3Ds: false
                    },
                    RetainOnSuccess: false
                },
                TransactionDetails: {
                    Payer: {
                        BillingAddress: data.Address,
                        CardholderName: null
                    }
                }
            };

            this.log("Legacy payload encountered and successfully updated."); //TODO-AWELLS: Debug helper - can remove.
        }
        return data;
    }

    handleNextActionData() {
        const nextActionData = this.nextActionData;
        console.log("current action:", "(passing challenge data to sdk)"); //TODO: REMOVE ME
        
        const nextAction = {
            action: "CHALLENGE",
            gateway: "stripe",
            stripe: true,
            key: nextActionData.dataset.key,
            data: nextActionData.dataset.value,
            transactionIntentId: nextActionData.dataset.transactionintentid,
            nextAction: nextActionData.dataset.type //"ThreeDsChallenge"
        };

        this.handleChallenge(nextAction);
    }

    disableForm(disable) {
        const form = document.querySelector("form");
        const inputs = form.getElementsByTagName("input");
        let length = inputs.length;
        while (length--) {
            inputs[length].disabled = disable;
        }
    }

    getTokenizationSuccessMessage() {
        let msg = this.token?.value;
        if (this.threedsCode?.value) {
            msg += `#3ds#${this.threedsCode.value}`;
        }
        return msg;
    }

    getReadyToCaptureMessage() {
        const result = {
            actionCode: "CAPTURE",
            succeeded: true,
            threeDSCode: this.threedsCode.value ?? null,
            transactionIntentId: this.transactionIntentId?.value
        };
        return JSON.stringify(result);
    }

    getValidationErrorMessage() {
        return this.validation?.value;
    }

    handleChallenge(data) {
        const payload = JSON.stringify(data);
        this.postOutboundMessage(payload, "dv-challenge");
    }

    handleFailure(data) {
        this.postOutboundMessage(data, "dv-response");
    }

    async handleConfirmAction(data) {
        this.disableForm(false);
        if (data.threeDSCode !== undefined) {
            utilities.setTargetValue("ThreeDSCode", data.threeDSCode);
        }
        await googleRecaptcha.invokeChallengeAsync("confirm");
        this.clickSubmit();
    }

    async cancel() {
        console.log("current action:", "CANCEL CHECKOUT"); //TODO: REMOVE ME
        this.disableForm(false);

        const url = new PayCentralURL(`widget/creditcard/checkout/${this.transactionIntentId.value}/cancel`);
        const response = await fetch(url, { method: 'POST', body: new FormData(document.forms[0]) });

        const responseData = await response.json();
        this.handleFailure(responseData);
    }

    /**
    * Event listener for incoming post messages.
    * @param {object} e - event
    */
    async tryReceivePostMessage(e) {
        const imisUrl = this.getTargetOrigin();

        if (e.origin === imisUrl || imisUrl === "*") {
            const data = e?.data;

            if (data) {
                const message = this.parsePaymentMessage(data);
                this.handleAction(message.action, message.data);
            }
        } else {
            console.log(`e.origin = ${e.origin} iMIS URL = ${imisUrl}`);
        }
    }

    /**
     * Parse incoming post message event data.
     * @param {any} data
     * @returns
     */
    parsePaymentMessage(data) {
        const message = {
            action: null,
            data: data
        };

        if (typeof data === "string" && data.startsWith("ping-") && data.length > 5) {
            message.action = "keepalive";
            message.data = "pong-" + data.substring(5);
        } else if (data === "Challenge successful") {
            message.action = "confirm";
        } else if (typeof data.action !== "undefined") {
            message.action = data.action;
        } else if (typeof data.DvGatewayID !== "undefined") {
            message.action = "checkout";
        } else if (typeof data.type !== "undefined") {
            message.action = data.type;
        }

        return message;
    }

    async handleAction(action, data) {
        try {
            console.log("current action:", action, JSON.stringify(data)); //TODO: REMOVE ME

            if (action) action = action.toUpperCase();
            switch (action) {
                case "CANCEL":
                    this.cancel();
                    break;
                case "CHECKOUT":
                    this.attemptSubmit(data);
                    break;
                case "CONFIRM":
                    await this.handleConfirmAction(data);
                    break;
                case "CURRENCY-AMOUNT":
                    if (window.paycentralstripe) {
                        window.paycentralstripe.updateAmountAndCurrency(data.amount, data.currency);
                    }
                    break;
                case "KEEPALIVE":
                    window.parent.postMessage(data, this.getTargetOrigin());
                    break;
                case "UI.LOAD":
                    // send read receipt
                    parent.postMessage({ type: "ack", id: data.id }, this.getTargetOrigin());
                    break;
                default:
                    console.warn(`Unknown action message received: ${action}`);
            }
        } catch (error) {
            throw new Error(`Call to 'handleNextAction' was unsuccessful. ${error.message}`);
        }
    }

    /**
     * PostOutboundMessage
     * Send a post message to parent window.
     * Prepend Widget post with  'dv-response:' so we can identify it iMIS side.
     * @param {boolean} isSuccess
     */
    postOutboundMessage(data, messagePrefix) {
        if (!messagePrefix) messagePrefix = "dv-response";
        const payload = `${messagePrefix}:${data}`;
        window.parent.postMessage(payload, this.getTargetOrigin());
    }
}
window.onload = () => {
    window.Widget = new Widget();
};
export default Widget;
